import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { message } from "antd";
import { Info, Paperclip, SendHorizontal, X } from "lucide-react";
import StyledTooltip from "src/components/reusable/StyledTooltip/StyledTooltip";
import ChatComposerPanel from "src/components/reusable/ChatComposerPanel";

interface ChatInputProps {
  input: string;
  setInput: React.Dispatch<React.SetStateAction<string>>;
  handleSendMessage: (msg?: string) => void;
  isFetching: boolean;
  isInputLocked?: boolean;
  inputHelperText?: string;
  selectedFile: File | File[] | null;
  setSelectedFile: React.Dispatch<React.SetStateAction<File | File[] | null>>;
  previewURL: string | null;
  setPreviewURL: React.Dispatch<React.SetStateAction<string | null>>;
  handlePaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void;
  autoCorrectInput?: (val: string) => string;
  showAttachmentHints?: boolean;
}

const ChatInput: React.FC<ChatInputProps> = ({
  input,
  setInput,
  handleSendMessage,
  isFetching,
  isInputLocked = false,
  inputHelperText,
  selectedFile,
  setSelectedFile,
  previewURL,
  setPreviewURL,
  autoCorrectInput,
  showAttachmentHints = false,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageUploadSuccessMessage = "Image uploaded successfully.";
  const fileUploadSuccessMessage = "File uploaded successfully.";
  const pasteImageOnlyMessage = "Only images file can be uploaded. Supported formats: JPEG, PNG, JPG.";
  const unsupportedFileMessage = "Only JPEG, PNG, JPG, PDF, or CSV files can be uploaded.";
  const allowedUploadExtensions = ["png", "jpg", "jpeg", "pdf", "csv"];
  const allowedUploadTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "application/pdf",
    "text/csv",
    "application/csv",
    "application/vnd.ms-excel",
  ];

  const isImageFile = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    return (
      file.type.startsWith("image/") ||
      ["png", "jpg", "jpeg"].includes(ext || "")
    );
  };

  const hasSelectedImage = () => {
    if (!selectedFile) {return false;}
    if (Array.isArray(selectedFile)) {return selectedFile.some(isImageFile);}
    return isImageFile(selectedFile);
  };

  const isAllowedUploadFile = (file: File) => {
    const ext = file.name.split(".").pop()?.toLowerCase();
    return (
      allowedUploadTypes.includes(file.type) ||
      (!!ext && allowedUploadExtensions.includes(ext))
    );
  };

  const handleFileUpload = (
    files: FileList | File | null,
    source: "attach" | "paste" = "attach",
  ): boolean => {
    if (!files) {return false;}

    const file = "length" in files ? files[0] : files;
    if (!file) {return false;}

    const isImage = isImageFile(file);

    if (source === "paste" && !isImage) {
      message.error(pasteImageOnlyMessage);
      return false;
    }

    if (!isAllowedUploadFile(file)) {
      message.error(unsupportedFileMessage);
      return false;
    }

    if (isImage && hasSelectedImage()) {
      message.warning("You can only upload one image at a time.");
      return false;
    }

    setSelectedFile(file);
    setPreviewURL(isImage ? URL.createObjectURL(file) : null);
    message.success(isImage ? imageUploadSuccessMessage : fileUploadSuccessMessage);
    return true;
  };

  const getFileFromDataTransfer = (dataTransfer: DataTransfer) => {
    if (dataTransfer.files && dataTransfer.files.length > 0) {
      return dataTransfer.files[0];
    }

    if (dataTransfer.items && dataTransfer.items.length > 0) {
      for (let i = 0; i < dataTransfer.items.length; i += 1) {
        const item = dataTransfer.items[i];
        if (item.kind === "file") {
          const file = item.getAsFile();
          if (file) {return file;}
        }
      }
    }

    return null;
  };

  const handleDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const file = getFileFromDataTransfer(e.dataTransfer);
    if (file) {
      handleFileUpload(file, "attach");
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const isInputDisabled = isFetching || isInputLocked;
  const isAttachDisabled = isInputDisabled || !!selectedFile;
  const attachTooltip = !!selectedFile
    ? t("chatScreen.removeFileBeforeAttachingTooltip")
    : t("chatScreen.attachFileTooltip");

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (e.clipboardData.files && e.clipboardData.files.length > 0) {
      e.preventDefault();
      handleFileUpload(e.clipboardData.files, "paste");
      return;
    }

    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          handleFileUpload(file, "paste");
          return;
        }
      }
    }
  };

  const removeFile = (index?: number) => {
    if (Array.isArray(selectedFile) && typeof index === "number") {
      const updatedFiles = selectedFile.filter((_, i) => i !== index);
      setSelectedFile(updatedFiles.length > 0 ? updatedFiles : null);
    } else {
      setSelectedFile(null);
    }
    setPreviewURL(null);
  };

  const hasComposerNotice = Boolean(inputHelperText?.trim());

  const handleSend = () => {
    if (!input.trim() && !selectedFile) {return;}

    setInput("");
    setSelectedFile(null);
    setPreviewURL(null);
    handleSendMessage(input);
  };

  return (
    <div
      className={`compact-input-area${
        hasComposerNotice ? " compact-input-area--composer-panel-host" : ""
      }`}
    >
      {selectedFile && (
        <div className="file-preview-container">
          {Array.isArray(selectedFile)
            ? selectedFile.map((file, index) => {
                const isImg = isImageFile(file);
                return (
                  <div
                    key={index}
                    className={`file-thumbnail ${isImg ? "file-thumbnail--image" : "file-thumbnail--document"}`}
                  >
                    {isImg ? (
                      <img
                        src={URL.createObjectURL(file)}
                        alt="preview"
                        className="thumbnail-img"
                      />
                    ) : (
                      <div className="file-chip">
                        <span className="file-icon" aria-hidden>
                          <Info size={14} />
                        </span>
                        <span className="file-name" title={file.name}>
                          {file.name}
                        </span>
                      </div>
                    )}
                    <button
                      type="button"
                      className={`remove-file-btn ${isImg ? "" : "remove-file-btn--inline"}`}
                      onClick={() => removeFile(index)}
                      aria-label={t("chatScreen.removeFileAriaLabel", { fileName: file.name })}
                    >
                      <X size={12} />
                    </button>
                  </div>
                );
              })
            : (
                <div
                  className={`file-thumbnail ${previewURL ? "file-thumbnail--image" : "file-thumbnail--document"}`}
                >
                  {previewURL ? (
                    <img src={previewURL} alt="Preview" className="thumbnail-img" />
                  ) : (
                    <div className="file-chip">
                      <span className="file-icon" aria-hidden>
                        <Info size={14} />
                      </span>
                      <span className="file-name" title={selectedFile.name}>
                        {selectedFile.name}
                      </span>
                    </div>
                  )}
                  <button
                    type="button"
                    className={`remove-file-btn ${previewURL ? "" : "remove-file-btn--inline"}`}
                    onClick={() => removeFile()}
                    aria-label={t("chatScreen.removeFileAriaLabel", { fileName: selectedFile.name })}
                  >
                    <X size={12} />
                  </button>
                </div>
              )}
        </div>
      )}

      <ChatComposerPanel noticeText={hasComposerNotice ? inputHelperText : null}>
        <div className="input-row">
        <input
          type="text"
          placeholder={t("chatScreen.askMeAnythingPlaceholder")}
          value={input}
          onPaste={handlePaste}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onChange={e => {
            const value = e.target.value;
            const lastChar = value.slice(-1);
            if (autoCorrectInput && [" ", ".", ",", "!", "?"].includes(lastChar)) {
              setInput(autoCorrectInput(value));
            } else {
              setInput(value);
            }
          }}
          onKeyDown={e => e.key === "Enter" && handleSend()}
          disabled={isInputDisabled}
        />

        <input
          type="file"
          ref={fileInputRef}
          onPaste={handlePaste}
          accept=".png,.jpg,.jpeg,.pdf,.csv,image/png,image/jpeg,application/pdf,text/csv"
          style={{ display: "none" }}
          disabled={isAttachDisabled}
          onChange={e => {
            handleFileUpload(e.target.files, "attach");
            e.currentTarget.value = "";
          }}
        />

        <StyledTooltip content={attachTooltip} placement="top">
          <span className="chat-input-attach-trigger">
            <button
              className={`chatbot-send-button chat-input-icon-button ${isAttachDisabled ? "disabled" : ""}`}
              onClick={() => {
                if (isAttachDisabled) {return;}
                fileInputRef.current?.click();
              }}
              disabled={isAttachDisabled}
              aria-label={t("chatScreen.attachAriaLabel")}
              title={attachTooltip}
            >
              <Paperclip size={18} className="clip-icon chat-input-icon" />
            </button>
          </span>
        </StyledTooltip>

        <button
          className={`chatbot-send-button chat-input-icon-button ${isInputDisabled ? "disabled" : ""}`}
          onClick={() => handleSendMessage()}
          disabled={isInputDisabled}
          aria-label={t("chatScreen.sendAriaLabel")}
        >
          <SendHorizontal size={18} className="send-icon chat-input-icon" />
        </button>
        </div>
      </ChatComposerPanel>

      {showAttachmentHints && (
        <div className="chat-input-attachment-hints" aria-label={t("chatScreen.attachmentHintsAriaLabel")}>
          <span className="chat-input-attachment-hint">
            <Info size={15} aria-hidden />
            {t("chatScreen.attachmentFormatHint")}
          </span>
          <span className="chat-input-attachment-hint">
            <Info size={15} aria-hidden />
            {t("chatScreen.oneFileAtATimeHint")}
          </span>
        </div>
      )}
    </div>
  );
};

export default ChatInput;
